import type { SetStateAction } from "react";

export interface ConversationSourceSelection {
  selectedSourceIndexes: Set<number>;
  onlyUseSelectedSources: boolean;
}

const EMPTY_CONVERSATION_SOURCE_SELECTION: ConversationSourceSelection = {
  selectedSourceIndexes: new Set<number>(),
  onlyUseSelectedSources: false,
};

export function getConversationSourceSelection(
  selections: Map<string, ConversationSourceSelection>,
  conversationKey: string,
): ConversationSourceSelection {
  return selections.get(conversationKey) ?? EMPTY_CONVERSATION_SOURCE_SELECTION;
}

export function updateConversationSelectedSourceIndexes(
  selections: Map<string, ConversationSourceSelection>,
  conversationKey: string,
  updater: SetStateAction<Set<number>>,
): Map<string, ConversationSourceSelection> {
  const current = getConversationSourceSelection(selections, conversationKey);
  const nextIndexes =
    typeof updater === "function" ? updater(current.selectedSourceIndexes) : updater;
  if (nextIndexes === current.selectedSourceIndexes) return selections;

  const next = new Map(selections);
  next.set(conversationKey, {
    ...current,
    selectedSourceIndexes: nextIndexes,
  });
  return next;
}

export function updateConversationOnlyUseSelectedSources(
  selections: Map<string, ConversationSourceSelection>,
  conversationKey: string,
  updater: SetStateAction<boolean>,
): Map<string, ConversationSourceSelection> {
  const current = getConversationSourceSelection(selections, conversationKey);
  const nextOnlyUseSelectedSources =
    typeof updater === "function" ? updater(current.onlyUseSelectedSources) : updater;
  if (nextOnlyUseSelectedSources === current.onlyUseSelectedSources) return selections;

  const next = new Map(selections);
  next.set(conversationKey, {
    ...current,
    onlyUseSelectedSources: nextOnlyUseSelectedSources,
  });
  return next;
}

export function migrateConversationSourceSelection(
  selections: Map<string, ConversationSourceSelection>,
  fromKey: string,
  toKey: string,
): Map<string, ConversationSourceSelection> {
  if (fromKey === toKey || !selections.has(fromKey)) return selections;

  const fromSelection = selections.get(fromKey)!;
  const next = new Map(selections);
  next.set(toKey, {
    ...fromSelection,
    selectedSourceIndexes: new Set(fromSelection.selectedSourceIndexes),
  });
  next.delete(fromKey);
  return next;
}

export function clearConversationSourceSelection(
  selections: Map<string, ConversationSourceSelection>,
  conversationKey: string,
): Map<string, ConversationSourceSelection> {
  if (!selections.has(conversationKey)) return selections;
  const next = new Map(selections);
  next.delete(conversationKey);
  return next;
}

export function isPendingConversationMigration(
  previousSelectedKey: string,
  selectedKey: string,
  pendingConversationId: string | null,
): boolean {
  return (
    previousSelectedKey === "add" &&
    pendingConversationId !== null &&
    String(pendingConversationId) === selectedKey
  );
}
